import React from 'react';
import { TrashIcon } from '@heroicons/react/24/outline';
import { MinusCircleIcon } from '@heroicons/react/20/solid';
import CurrencyInput from 'react-currency-input-field';
import SlideOver from '../../slide-over/slide-over';
import { TWButton, ToggleSwitch } from '../../ui';
import CaloriesInput from '../calories/calories-input';
import PosMappingSection from '../pos-mapping/pos-mapping-section';
import usePosMappingAccess from '../pos-mapping/use-pos-mapping-access';

function classNames(...classes) {
  return classes.filter(Boolean).join(' ');
}

export default function ItemUpsertModal({
  show,
  onFormChange,
  onFormSubmit,
  onFormSubmitMultiple,
  onClickDeleteItem,
  isSingleItem,
  setIsSingleItem,
  onCloseUpsert,
  items,
  itemList,
  setItems,
  itemForm,
  errors,
  loading,
}) {
  const { canEdit } = usePosMappingAccess();

  const onClickAddItem = () => {
    setItems((prev) => [...prev, { name: '', price: '' }]);
  };

  const onItemsChange = (val, key, index) => {
    const newItemList = [...items];
    newItemList[index][key] = val;
    setItems(newItemList);
  };

  const onClickDeleteItemInMultiple = (index) => {
    const newItemList = [...items];
    newItemList.splice(index, 1);
    setItems(newItemList);
  };

  const footerContent = (
    <div className="grid grid-cols-12 gap-3">
      {itemForm.id && (
        <TWButton
          variant="danger"
          icon={TrashIcon}
          disabled={loading}
          loading={loading}
          onClick={() => onClickDeleteItem(itemForm)}
          className="col-span-2 w-full"
        />
      )}
      <TWButton
        variant="default"
        label="Cancel"
        disabled={loading}
        onClick={() => onCloseUpsert()}
        className={classNames(
          itemForm.id ? 'col-span-5' : 'col-span-6',
          'w-full',
        )}
      />
      {isSingleItem ? (
        <TWButton
          variant="primary"
          label={itemForm.id ? 'Edit Item' : 'Add Item'}
          disabled={loading}
          loading={loading}
          onClick={() => onFormSubmit()}
          className={classNames(
            itemForm.id ? 'col-span-5' : 'col-span-6',
            'w-full',
          )}
        />
      ) : (
        <TWButton
          variant="primary"
          label="Add Items"
          disabled={loading}
          loading={loading}
          onClick={() => onFormSubmitMultiple()}
          className={classNames(
            itemForm.id ? 'col-span-5' : 'col-span-6',
            'w-full',
          )}
        />
      )}
    </div>
  );

  return (
    <SlideOver
      show={show}
      onClose={onCloseUpsert}
      title={itemForm.id ? 'Edit Item' : 'Create Item'}
      subtitle="Define item names, prices and ordering for this product."
      footer={footerContent}
    >
      <div className="space-y-4">
        {itemList.length === 0 && (
          <div className="flex space-x-5">
            <h1
              onClick={() => setIsSingleItem(true)}
              className={classNames(
                isSingleItem ? 'border-blue-500' : '',
                'cursor-pointer border-b-2 inline-block'
              )}
            >
              Single Item
            </h1>
            <h1
              onClick={() => setIsSingleItem(false)}
              className={classNames(
                isSingleItem ? '' : 'border-blue-500',
                'cursor-pointer border-b-2 inline-block'
              )}
            >
              Multiple Item
            </h1>
          </div>
        )}
        {isSingleItem ? (
          <>
            <div>
              <label
                htmlFor="name"
                className="block text-sm font-medium text-gray-700 dark:text-slate-400"
              >
                Item Name
              </label>
              <div className="mt-1">
                <input
                  value={itemForm.name}
                  onChange={(e) => {
                    onFormChange(e.target.value, 'name');
                  }}
                  maxLength={60}
                  type="text"
                  name="name"
                  id="name"
                  className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25 focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                  placeholder="e.g ''Chicken,beef'' or ''Small, Medium''"
                />
                {errors.name && (
                  <div className="h-2 mt-1 text-xs">
                    <p className="text-red-700">
                      Item name can not be empty
                    </p>
                  </div>
                )}
              </div>
            </div>
            <div>
              <p className="font-normal text-sm dark:text-slate-400 text-gray-500">
                Customize your items with options like "chicken, beef,
                lamb" or "small, medium, and large." For items without
                customizations, simply enter the item name.
              </p>
            </div>
            {/* Show calories is the master switch (= showcalories). Off hides the
                input and sends showcalories:false; the entered value is kept in
                form state so re-enabling restores it — no data loss. showcalories
                governs every customer surface (kiosk, customerapp, order-react),
                so the copy stays surface-neutral. */}
            {/* Price sits on its own line. The Show-calories switch lives below
                it and, when on, reveals the Calories input stacked beneath the
                switch. Both numeric fields share a compact w-44 rather than
                stretching across the wide drawer. */}
            <div className="space-y-4">
              <div className="w-44">
                <label
                  htmlFor="input-price"
                  className="block text-sm font-medium leading-6 text-gray-900 dark:text-slate-400"
                >
                  Price
                </label>
                <div className="relative mt-2 rounded-md shadow-sm">
                  <CurrencyInput
                    id="input-price"
                    name="price"
                    placeholder="0.00"
                    className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25 focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                    prefix="$"
                    value={itemForm.price}
                    decimalSeparator="."
                    groupSeparator=","
                    decimalScale={2}
                    step={1}
                    defaultValue={0}
                    decimalsLimit={2}
                    onValueChange={(value, name, values) =>
                      onFormChange(value, name)
                    }
                  />
                </div>
                {errors.price && (
                  <div className="h-2 mt-1 text-xs">
                    <p className="text-red-700">Price can not be empty</p>
                  </div>
                )}
              </div>
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <ToggleSwitch
                    checked={itemForm.showcalories !== false}
                    ariaLabel="Show calories"
                    onChange={(val) => onFormChange(val, 'showcalories')}
                  />
                  <span className="text-sm font-medium text-gray-700 dark:text-slate-400">
                    Show calories
                  </span>
                </div>
                {itemForm.showcalories !== false && (
                  <div className="w-44">
                    <label
                      htmlFor="calories"
                      className="block text-sm font-medium leading-6 text-gray-900 dark:text-slate-400"
                    >
                      Calories{' '}
                      <span className="font-normal text-gray-400">
                        (optional)
                      </span>
                    </label>
                    <div className="mt-2">
                      <CaloriesInput
                        id="calories"
                        value={itemForm.calories}
                        onChange={(val) => onFormChange(val, 'calories')}
                      />
                    </div>
                  </div>
                )}
              </div>
            </div>
            <PosMappingSection
              level="item"
              squareid={itemForm.squareid}
              squarename={itemForm.squarename}
              squaremissing={itemForm.squaremissing}
              onChange={(id, name) => {
                onFormChange(id, 'squareid');
                onFormChange(name, 'squarename');
              }}
            />
            {canEdit && itemForm.squareid && (
              <div className="flex items-center gap-3">
                <ToggleSwitch
                  checked={itemForm.usesquareprice !== false}
                  ariaLabel="Use Square price"
                  onChange={(val) => onFormChange(val, 'usesquareprice')}
                />
                <span className="text-sm font-medium text-gray-700 dark:text-slate-400">
                  Use Square Price
                </span>
              </div>
            )}
            {itemForm.squareid && itemForm.usesquareprice === false && (
              <p className="text-xs text-amber-600 dark:text-amber-300">
                This item uses a custom Thisway price and may be different from
                Square POS.
              </p>
            )}
          </>
        ) : (
          <>
            {items.map((item, index) => (
              <div key={index} className="grid grid-cols-12 space-x-5">
                <div className="col-span-1 self-center justify-self-center">
                  <MinusCircleIcon
                    onClick={() => onClickDeleteItemInMultiple(index)}
                    className="text-blue-500 mr-3 w-5 h-5 hover:text-blue-900"
                  />
                </div>
                <div className="col-span-5">
                  <label
                    htmlFor="name"
                    className="block text-sm font-medium text-gray-700 dark:text-slate-400"
                  >
                    Item Name
                  </label>
                  <div className="mt-1">
                    <input
                      value={item.name}
                      onChange={(e) =>
                        onItemsChange(e.target.value, 'name', index)
                      }
                      type="text"
                      name="name"
                      id="name"
                      className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25 focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                      placeholder="e.g ''Chicken,beef'' or ''Small, Medium''"
                    />
                    <div className="h-2 mt-1 text-xs">
                      {errors.name && (
                        <p className="text-red-700">
                          Item name can not be empty
                        </p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="col-span-3">
                  <label
                    htmlFor="price"
                    className="block text-sm font-medium leading-6 text-gray-900"
                  >
                    Price
                  </label>
                  <div className="relative mt-2 rounded-md shadow-sm">
                    <CurrencyInput
                      id="input-price"
                      name="price"
                      placeholder="0.00"
                      className="block w-full appearance-none rounded-md border dark:text-slate-200 dark:bg-slate-800 border-gray-300 px-3 py-2 placeholder-black placeholder-opacity-25 focus:border-blue-500 focus:outline-none focus:ring-blue-500 sm:text-sm dark:focus:border-sky-400 dark:focus:ring-sky-400 dark:placeholder:text-white dark:border-slate-500"
                      prefix="$"
                      value={item.price}
                      decimalSeparator="."
                      groupSeparator=","
                      decimalScale={2}
                      step={1}
                      defaultValue={0}
                      decimalsLimit={2}
                      onValueChange={(value, name, values) =>
                        onItemsChange(value, name, index)
                      }
                    />
                  </div>
                  {errors.price && (
                    <div className="h-2 mt-1 text-xs">
                      <p className="text-red-700">
                        Price can not be empty
                      </p>
                    </div>
                  )}
                </div>
                <div className="col-span-3">
                  <label
                    htmlFor={`calories-${index}`}
                    className="block text-sm font-medium leading-6 text-gray-900 dark:text-slate-400"
                  >
                    Calories
                  </label>
                  <div className="mt-2">
                    <CaloriesInput
                      id={`calories-${index}`}
                      value={item.calories}
                      onChange={(val) => onItemsChange(val, 'calories', index)}
                    />
                  </div>
                </div>
              </div>
            ))}
            <div>
              <p className="font-normal text-sm dark:text-slate-400">
                Customize your items with options like "chicken, beef,
                lamb" or "small, medium, and large." For items without
                customizations, simply enter the item name.
              </p>
            </div>
            <div className="sm:grid z-10 sm:grid-flow-row-dense sm:grid-cols-4 sm:gap-3">
              <TWButton
                variant="primary"
                label="+ Add Item"
                disabled={loading.upsertLoading}
                loading={loading.upsertLoading}
                onClick={() => onClickAddItem()}
                className="col-span-1 col-start-1 w-full"
              />
            </div>
          </>
        )}
      </div>
    </SlideOver>
  );
}
